import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Database,
  Download,
  HardDrive,
  KeyRound,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  TriangleAlert
} from "lucide-react";
import { api, ApiError } from "../lib/api";
import { formatDate } from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/Button";
import { PageLoading } from "../components/Loading";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function SettingsPage() {
  useDocumentTitle("设置");
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const sync = useMutation({
    mutationFn: api.syncCatalog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    }
  });
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);

  useEffect(() => {
    function capture(event: Event) {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    }
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  if (settings.isLoading) return <PageLoading label="正在读取云端设置" />;

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordBusy(true);
    setPasswordMessage("");
    try {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setPasswordMessage("密码已更新");
    } catch (error) {
      setPasswordMessage(error instanceof ApiError ? error.message : "修改失败");
    } finally {
      setPasswordBusy(false);
    }
  }

  const data = settings.data;

  return (
    <div className="settings-page page-frame">
      <header className="settings-heading">
        <span className="eyebrow">PRIVATE SYSTEM</span>
        <h1>学习室设置</h1>
        <p>视频、转写和翻译都在你的私有云端完成。这里显示真实的服务状态。</p>
      </header>

      <div className="settings-grid">
        <section className="settings-card settings-card-wide">
          <div className="settings-card-heading">
            <div className="settings-icon">
              <Server size={19} />
            </div>
            <div>
              <span className="eyebrow">SYSTEM READINESS</span>
              <h2>处理引擎</h2>
            </div>
          </div>
          <div className="status-grid">
            <StatusLine
              label="DeepSeek 翻译"
              ready={Boolean(data?.translationReady)}
              detail={data?.translationReady ? "API Key 已配置" : "等待 .env 中的 Key"}
            />
            <StatusLine
              label="FFmpeg"
              ready={Boolean(data?.ffmpegConfigured)}
              detail={data?.ffmpegConfigured ? "媒体工具可用" : "尚未安装或不在 PATH"}
            />
            <StatusLine
              label="Whisper 模型"
              ready={Boolean(data?.whisperModel)}
              detail={data?.whisperModel || "未配置"}
            />
          </div>
          <div className="settings-detail-row">
            <span>
              <HardDrive size={15} />
              数据目录
            </span>
            <code>{data?.dataDir}</code>
          </div>
          <div className="settings-detail-row">
            <span>
              <Database size={15} />
              最近目录同步
            </span>
            <code>{formatDate(data?.catalogLastSync)}</code>
          </div>
          {data?.catalogLastError && (
            <div className="inline-alert">
              <TriangleAlert size={15} />
              {data.catalogLastError}
            </div>
          )}
          <Button
            variant="secondary"
            icon={<RefreshCw size={15} />}
            loading={sync.isPending}
            onClick={() => sync.mutate()}
          >
            立即同步 TED 目录
          </Button>
        </section>

        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-icon">
              <Smartphone size={19} />
            </div>
            <div>
              <span className="eyebrow">ANDROID ACCESS</span>
              <h2>装到手机</h2>
            </div>
          </div>
          <p>
            使用已安装的 TED 双语精听 Android 应用，或直接在手机浏览器打开当前 HTTPS 地址。
          </p>
          <div className="settings-instruction">
            <span>当前地址</span>
            <code>{window.location.origin}</code>
          </div>
          {installPrompt ? (
            <Button
              variant="primary"
              icon={<Download size={15} />}
              onClick={async () => {
                await installPrompt.prompt();
                await installPrompt.userChoice;
                setInstallPrompt(null);
              }}
            >
              安装此应用
            </Button>
          ) : (
            <p className="settings-note">
              浏览器菜单 → “添加到主屏幕”。若浏览器不支持独立 PWA 窗口，仍会创建可用的主屏快捷方式。
            </p>
          )}
          <p className="settings-note">
            手机只连接自己的云端学习室，不访问 YouTube；电脑关机后仍可继续使用。
          </p>
        </section>

        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-icon">
              <KeyRound size={19} />
            </div>
            <div>
              <span className="eyebrow">ACCESS</span>
              <h2>更换密码</h2>
            </div>
          </div>
          <form className="password-form" onSubmit={changePassword}>
            <label>
              <span>当前密码</span>
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
              />
            </label>
            <label>
              <span>新密码</span>
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            {passwordMessage && (
              <p className={passwordMessage === "密码已更新" ? "form-success" : "form-error"}>
                {passwordMessage}
              </p>
            )}
            <Button type="submit" variant="secondary" loading={passwordBusy}>
              更新密码
            </Button>
          </form>
        </section>

        <section className="settings-card settings-card-wide privacy-card">
          <div className="settings-card-heading">
            <div className="settings-icon">
              <ShieldCheck size={19} />
            </div>
            <div>
              <span className="eyebrow">DATA BOUNDARY</span>
              <h2>数据去哪了</h2>
            </div>
          </div>
          <div className="privacy-columns">
            <div>
              <CheckCircle2 size={17} />
              <strong>留在私有云端</strong>
              <p>完整视频、音轨、英语转写、学习进度和手工字幕修改。</p>
            </div>
            <div>
              <CheckCircle2 size={17} />
              <strong>发给 DeepSeek</strong>
              <p>仅发送需要翻译的英文句子文本，不发送视频或音频。</p>
            </div>
            <div>
              <CheckCircle2 size={17} />
              <strong>手机在线访问</strong>
              <p>手机通过专属 HTTPS 地址播放，不连接 YouTube，也不在本地长期保存视频。</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatusLine({
  label,
  ready,
  detail
}: {
  label: string;
  ready: boolean;
  detail: string;
}) {
  return (
    <div className={`status-line ${ready ? "is-ready" : "is-waiting"}`}>
      {ready ? <CheckCircle2 size={18} /> : <TriangleAlert size={18} />}
      <div>
        <strong>{label}</strong>
        <span>{detail}</span>
      </div>
    </div>
  );
}

